
import Vue from 'vue';
import { mapActions, mapState } from 'vuex';
import {
	getRunnitsSelectedColumnsVal,
	getRunnitsSelectedImageModeVal,
	setRunnitsSelectedColumnsVal,
	setRunnitsSelectedImageModeVal,
	setRunnitsSelectedPromptView,
	getRunnitsSelectedPromptView,
} from '@/utils';
import { deriveSelectedColumnsSpan } from '@/utils/galleryColumns';
import {
	SELECTED_COLUMNS_MAP,
	SELECTED_IMAGE_MODE,
	SELECTED_IMAGE_MODE_TILED_LABEL,
	SELECTED_IMAGE_MODE_INFO_LABEL,
	SELECTED_COLUMN_MENU_ITEMS,
	SELECTED_IMAGE_MODE_MENU_ITEMS,
	NODE_RUN_RESULT_SELECTION_MODE,
	SELECTED_PROMPT_VIEW,
} from '@/views/Runnits/constants';
import { RunnitsImageSelectMixin } from '@/mixins/RunnitsImageSelectMixin';
import { RunnitBulkActionsMixin } from '@/mixins/RunnitBulkActionsMixin';
import FeaturedNodeDefResultsContainer from '@/views/Runnits/FeaturedNodeDefResultsContainer.vue';
import EmptyState from '@/components/states/EmptyState.vue';
import RunnitDialog from '@/views/Runnits/base/RunnitDialog.vue';
import { RUNNIT_NODE_DEF_TOOL_APP_TYPE, RUNNIT_TYPE } from '@run-diffusion/shared';
import MenuOrBottomSheet from '@/components/base/MenuOrBottomSheet.vue';
import SliderSelect from '@/components/base/SliderSelect.vue';
import PromptFormSVG from '@/assets/PromptFormSVG.vue';
import DrawerFormSVG from '@/assets/DrawerFormSVG.vue';

export default Vue.extend({
	name: 'RunnitPageSettings',
	props: {
		iconOnly: { type: Boolean, default: false },
		iconColor: { type: String, default: 'rgba(255, 255, 255, 0.7)' },
		hideImageMode: { type: Boolean, default: false },
		hideImageSize: { type: Boolean, default: false },
		hideSelectionMode: { type: Boolean, default: false },
		selectedNodeDef: { type: Object, default: null },
		currentPageType: { type: String, default: null },
	},
	mixins: [
		RunnitsImageSelectMixin,
		RunnitBulkActionsMixin,
	],
	data () {
		return {
			SELECTED_PROMPT_VIEW,
			RUNNIT_TYPE,
			SELECTED_IMAGE_MODE,
			SELECTED_IMAGE_MODE_TILED_LABEL,
			SELECTED_IMAGE_MODE_INFO_LABEL,
			NODE_RUN_RESULT_SELECTION_MODE,
			RUNNIT_NODE_DEF_TOOL_APP_TYPE,
			selectedImageModeMenuItems: SELECTED_IMAGE_MODE_MENU_ITEMS,

			selectedButtonStyles: {
				position: 'relative',
				borderRadius: '8px',
				background: `
					linear-gradient(var(--grey-875), var(--grey-875)) padding-box,
					linear-gradient(202deg, var(--primary-blue) 30%, var(--primary-purple), var(--primary-pink)) border-box
				`,
				border: '2px solid transparent',
			},

			featuredResultsDialogOpen: false,
			hasFeaturedNodeDefResults: false,
		};
	},
	computed: {
		...mapState([
			'runnitState',
			'user',
			'runnitNodesMap',
		]),
		showFeaturedRemixButton () {
			return this.selectedNodeDef && this.selectedNodeDef.showFeatured && this.selectedNodeDef.appType !== RUNNIT_NODE_DEF_TOOL_APP_TYPE.TRAINER && this.currentPageType !== 'template';
		},
		showBulkActionsButton () {
			return this.currentPageType !== 'template';
		},
		columnItems () {
			return Object.keys(SELECTED_COLUMNS_MAP).map(key => ({
				value: Number(key),
				label: SELECTED_COLUMNS_MAP[key],
			}));
		},
		/**
		 * The gallery's default column span, derived from the width actually AVAILABLE to the
		 * content rather than the raw viewport. A 1700px viewport stayed at 6 columns with the
		 * settings drawer open even though the board only had ~1200px, making tiles far narrower
		 * than the column count implied.
		 *
		 * `$vuetify.application` is Vuetify's own record of what each `app` drawer has reserved,
		 * and it is reactive (v-main lays the page out from these same numbers). Reading it means
		 * this never has to guess: it already accounts for the Runnits side nav (260px expanded,
		 * 60px mini, 0 when it overlays on mobile), the 400px gallery drawer in app mode, and the
		 * 500px settings drawer, and it counts a drawer only while that drawer is really mounted
		 * and reflowing. Subtracting a hardcoded 500 keyed off `settingsDrawerOpen` alone got both
		 * halves wrong: it ignored the other two drawers, and it kept subtracting while the
		 * settings drawer was unmounted (RunnitWorkflow.vue renders it only with a node selected
		 * and no batch wizard open) or was a bottom sheet reserving nothing.
		 *
		 * `deriveSelectedColumnsSpan` itself stays a pure function of a width; only this input
		 * changes.
		 */
		derivedSelectedColumns (): number {
			const { left, right } = this.$vuetify.application;
			return deriveSelectedColumnsSpan(this.$vuetify.breakpoint.width - left - right);
		},
		isSingleTool () {
			return this._get(this.runnitState, 'runnit.type') === RUNNIT_TYPE.SINGLE_TOOL;
		},
		showPromptDrivenFormOption () {
			const selectedNodeDef = this.runnitState.selectedNodeId ? this.runnitNodesMap[this.runnitState.selectedNodeId]?.nodeDef : null
			return !!selectedNodeDef?.usePromptDrivenForm // node def allows the feature
				&& this.isSingleTool; // only single tool runnits can use the feature
		},
	},
	watch: {
		// While the user has made no explicit choice, the default column count tracks the
		// available width. `getRunnitsSelectedColumnsVal()` is read at fire time (not cached on
		// a component-local flag) because either column selector, this component's or the
		// sibling SelectedColumnsButton's, can persist a choice; the stored value wins on reload.
		derivedSelectedColumns: {
			immediate: true,
			handler (selectedColumns: number) {
				if (getRunnitsSelectedColumnsVal()) return;
				this.updateRunnitState({ selectedColumns });
			},
		},
	},
	mounted () {
		const localSelectedImageMode: string = getRunnitsSelectedImageModeVal();
		const localSelectedColumns: number = getRunnitsSelectedColumnsVal();
		const localSelectedPromptView: string = getRunnitsSelectedPromptView();

		if (localSelectedImageMode || localSelectedColumns || localSelectedPromptView) {
			this.updateRunnitState({
				...(localSelectedImageMode && {
					selectedImageMode: localSelectedImageMode,
				}),
				...(localSelectedColumns && {
					selectedColumns: localSelectedColumns,
				}),
				...(localSelectedPromptView && {
					selectedPromptView: localSelectedPromptView,
				}),
			});
		}
	},
	methods: {
		...mapActions([
			'updateRunnitState',
			'updateSnackbar',
			'incrementToggleRunnitWorkflowAllToolsTrigger',
		]),
		onSelectImageMode (imageMode: string) {
			this.updateRunnitState({
				selectedImageMode: imageMode,
			});
			setRunnitsSelectedImageModeVal(imageMode);
		},
		onSelectColumns (selectedColumns: number) {
			this.updateRunnitState({
				selectedColumns: selectedColumns,
			});
			setRunnitsSelectedColumnsVal(selectedColumns);
		},
		toggleImageGalleryDrawer () {
			this.updateRunnitState({
				imageGalleryDrawerOpen: !this.runnitState.imageGalleryDrawerOpen,
			});
		},
		onFeaturedNodeDefResultsCheck (hasResults: boolean) {
			this.hasFeaturedNodeDefResults = hasResults;
		},
		onSelectPromptView (promptView: string) {
			this.updateRunnitState({
				selectedPromptView: promptView,
			});
			setRunnitsSelectedPromptView(promptView);
		},
		onCollapseAllTools () {
			this.incrementToggleRunnitWorkflowAllToolsTrigger({ collapseAll: true });
		},
		onOpenAllTools () {
			this.incrementToggleRunnitWorkflowAllToolsTrigger({ expandAll: true });
		},
	},
	components: {
		FeaturedNodeDefResultsContainer,
		EmptyState,
		RunnitDialog,
		MenuOrBottomSheet,
		SliderSelect,
		PromptFormSVG,
		DrawerFormSVG,
	},
});
